import { useGetUrl } from '@seek/ca-myprofile-utils';
import { useTranslations } from '@vocab/react';
import {
  Text,
  Drawer,
  TextLink,
  Stack,
  Divider,
  Inline,
  Button,
} from 'braid-design-system';
import { useEffect } from 'react';

import { CardError } from 'src/components/Card/CardError/CardError';
import { PROFILE_VISIBILITY_V2 } from 'src/components/Router';
import { useEnterReadMode } from 'src/components/Router/useEnterReadMode';
import { useAppConfig } from 'src/modules/appConfig';
import { hotjarEvent } from 'src/modules/hotjar';
import { useRetryAction } from 'src/modules/retry-actions/hooks';

import translations from './.vocab';
import { EditProfileVisibilitySettings } from './EditProfileVisibilitySettings';
import { ShareableProfileSettings } from './EditShareableProfileSettings';

interface FormProps {
  isOpen: boolean;
}

export const EditProfileVisibility = ({ isOpen }: FormProps) => {
  const { t } = useTranslations(translations);
  const { zone, language, isUnifiedSite } = useAppConfig();
  const getUrl = useGetUrl(zone, language, isUnifiedSite);
  const retryAction = useRetryAction(PROFILE_VISIBILITY_V2);
  const showErrorAlert = Boolean(retryAction?.canRetry);
  const enterReadMode = useEnterReadMode();

  useEffect(() => {
    hotjarEvent('profile-visibility-drawer-opened');
  }, []);

  return (
    <Drawer
      id="profile-visibility-settings"
      data={{ automation: 'profile-visibility-settings' }}
      title={t('Profile visibility settings')}
      description={
        <Text tone="secondary">
          {t('For all settings, your Profile including...', {
            TextLink: (children: React.ReactNode[]) => (
              <TextLink
                key="privacyPolicyPath"
                target="_blank"
                href={getUrl('profile-privacy')}
                hitArea="large"
                weight="weak"
              >
                {children}
              </TextLink>
            ),
          })}
        </Text>
      }
      open={isOpen}
      onClose={enterReadMode}
      closeLabel={t('Close')}
      width="small"
    >
      {showErrorAlert && <CardError card={PROFILE_VISIBILITY_V2} />}
      <Divider />
      <Stack space="large">
        <EditProfileVisibilitySettings />
        <Divider />
        <ShareableProfileSettings />
        <Inline space="small" collapseBelow="tablet">
          <Button
            id="done-btn"
            tone="formAccent"
            data={{ automation: 'done-btn' }}
            onClick={enterReadMode}
          >
            {t('Done')}
          </Button>
        </Inline>
      </Stack>
    </Drawer>
  );
};
